import { initFirebase } from '../firebase'
import {
  collection, getDocs, doc, getDoc, setDoc, addDoc, updateDoc,
  serverTimestamp, query, orderBy, deleteDoc, increment
} from 'firebase/firestore'
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth'

const { db, auth } = initFirebase()

let _authReadyPromise = null
export function waitForAuth(timeoutMs = 8000) {
  if (auth.currentUser) return Promise.resolve(auth.currentUser)
  if (_authReadyPromise) return _authReadyPromise
  _authReadyPromise = new Promise((resolve, reject) => {
    let resolved = false
    const timer = setTimeout(async () => {
      if (resolved) return
      try {
        const cred = await signInAnonymously(auth)
        resolved = true
        resolve(cred.user)
      } catch (e) {
        reject(e)
      }
    }, timeoutMs)
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        clearTimeout(timer)
        unsub()
        resolved = true
        resolve(user)
        return
      }
      try {
        const cred = await signInAnonymously(auth)
        clearTimeout(timer)
        unsub()
        resolved = true
        resolve(cred.user)
      } catch (e) {
        // wait for timer fallback
      }
    })
  })
  return _authReadyPromise
}

const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10)
}

const stripUndefined = (obj) => {
  if (obj == null || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(stripUndefined)
  const out = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue
    if (v !== null && typeof v === 'object' && !Array.isArray(v) && !(v && v.toMillis) && !(v && v._methodName)) {
      out[k] = stripUndefined(v)
    } else {
      out[k] = v
    }
  }
  return out
}

export class FirebaseVerifyService {
  async createCheck({ label, notes }) {
    await waitForAuth()
    const id = generateId()
    const ref = doc(db, 'verification_checks', id)
    await setDoc(ref, {
      label: label || '',
      notes: notes || '',
      createdAt: serverTimestamp(),
      clicksCount: 0
    })
    return id
  }

  async listChecks() {
    await waitForAuth()
    const ref = collection(db, 'verification_checks')
    const q = query(ref, orderBy('createdAt', 'desc'))
    const snap = await getDocs(q)
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
  }

  async getCheck(id) {
    if (!id) return null
    await waitForAuth()
    const ref = doc(db, 'verification_checks', id)
    const snap = await getDoc(ref)
    if (!snap.exists()) return null
    return { id: snap.id, ...snap.data() }
  }

  async getClicks(checkId) {
    if (!checkId) return []
    await waitForAuth()
    const ref = collection(db, 'verification_checks', checkId, 'clicks')
    const q = query(ref, orderBy('clickedAt', 'desc'))
    const snap = await getDocs(q)
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
  }

  async recordClick(checkId, data) {
    if (!checkId) return
    await waitForAuth()
    const subRef = collection(db, 'verification_checks', checkId, 'clicks')
    const cleaned = stripUndefined({ ...data, clickedAt: serverTimestamp() })
    await addDoc(subRef, cleaned)
    const parentRef = doc(db, 'verification_checks', checkId)
    const parentPatch = {
      clicksCount: increment(1),
      lastClickAt: serverTimestamp()
    }
    if (!data.gpsOnly) {
      parentPatch.lastVerdict = data.verdict || 'unknown'
      parentPatch.lastCountry = data.country || null
      parentPatch.lastIp = data.ip || null
    }
    try {
      await updateDoc(parentRef, parentPatch)
    } catch (e) {
      console.warn('Failed to update parent counters (non-fatal):', e?.message || e)
    }
  }

  async deleteCheck(id) {
    if (!id) return
    await waitForAuth()
    await deleteDoc(doc(db, 'verification_checks', id))
  }
}
